import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { X, MapPin, Lock, Check, ChevronRight, Star, Plane } from "lucide-react";
import { LEVELS } from "@/config/levels";

// World configurations with coordinates for the map
// Redesigned layout: organic island-chain style with varied spacing
const WORLDS = [
  // === STARTING REGION (Bottom-left cluster) ===
  {
    id: 1,
    name: "Fruit Garden",
    subtitle: "The Beginning",
    levels: [1, 50],
    emoji: "🌸",
    color: "#22c55e",
    unlockLevel: 1,
    position: { x: 8, y: 90 },
    region: "starter"
  },
  {
    id: 2,
    name: "Ice World",
    subtitle: "Frozen Frontier",
    levels: [51, 60],
    emoji: "🧊",
    color: "#06b6d4",
    unlockLevel: 51,
    position: { x: 22, y: 82 },
    region: "starter"
  },
  {
    id: 3,
    name: "Chain Realm",
    subtitle: "Lock & Key",
    levels: [61, 70],
    emoji: "⛓️",
    color: "#64748b",
    unlockLevel: 61,
    position: { x: 38, y: 88 },
    region: "starter"
  },
  
  // === TROPICAL REGION (Middle-right area) ===
  {
    id: 4,
    name: "Chocolate Factory",
    subtitle: "Sweet Chaos",
    levels: [71, 80],
    emoji: "🍫",
    color: "#d97706",
    unlockLevel: 71,
    position: { x: 55, y: 78 },
    region: "tropical"
  },
  {
    id: 5,
    name: "Honey Hive",
    subtitle: "Sticky Situation",
    levels: [81, 90],
    emoji: "🍯",
    color: "#eab308",
    unlockLevel: 81,
    position: { x: 72, y: 85 },
    region: "tropical"
  },
  {
    id: 6,
    name: "Lava World",
    subtitle: "Volcanic Fury",
    levels: [91, 100],
    emoji: "🌋",
    color: "#dc2626",
    unlockLevel: 91,
    position: { x: 88, y: 76 },
    region: "tropical"
  },
  
  // === MYSTIC REGION (Upper-right descending) ===
  {
    id: 7,
    name: "Point World",
    subtitle: "Score Master",
    levels: [101, 120],
    emoji: "🎯",
    color: "#8b5cf6",
    unlockLevel: 101,
    position: { x: 92, y: 60 },
    region: "mystic"
  },
  {
    id: 8,
    name: "Dark Forest",
    subtitle: "Shadow Realm",
    levels: [121, 140],
    emoji: "🦉",
    color: "#1e293b",
    unlockLevel: 121,
    position: { x: 78, y: 52 },
    region: "mystic"
  },
  {
    id: 9,
    name: "Ocean Depths",
    subtitle: "Deep Sea",
    levels: [141, 160],
    emoji: "🌊",
    color: "#0284c7",
    unlockLevel: 141,
    position: { x: 62, y: 58 },
    region: "mystic"
  },
  {
    id: 10,
    name: "Frozen Peaks",
    subtitle: "Arctic Challenge",
    levels: [161, 180],
    emoji: "🏔️",
    color: "#38bdf8",
    unlockLevel: 161,
    position: { x: 48, y: 48 },
    region: "mystic"
  },
  
  // === ANCIENT REGION (Left side, going up) ===
  {
    id: 11,
    name: "Ancient Ruins",
    subtitle: "Temple Mysteries",
    levels: [181, 200],
    emoji: "🏛️",
    color: "#b45309",
    unlockLevel: 181,
    position: { x: 28, y: 55 },
    region: "ancient"
  },
  {
    id: 12,
    name: "Volcanic Core",
    subtitle: "Heart of Fire",
    levels: [201, 220],
    emoji: "🔥",
    color: "#ef4444",
    unlockLevel: 201,
    position: { x: 12, y: 62 },
    region: "ancient"
  },
  {
    id: 13,
    name: "Moonlight Garden",
    subtitle: "Day & Night",
    levels: [221, 240],
    emoji: "🌙",
    color: "#6366f1",
    unlockLevel: 221,
    position: { x: 8, y: 45 },
    region: "ancient"
  },
  
  // === MAGICAL REGION (Center cluster) ===
  {
    id: 14,
    name: "Alchemist's Lab",
    subtitle: "Potion Master",
    levels: [241, 260],
    emoji: "⚗️",
    color: "#10b981",
    unlockLevel: 241,
    position: { x: 25, y: 38 },
    region: "magical"
  },
  {
    id: 15,
    name: "Circus Carnival",
    subtitle: "Fun & Games",
    levels: [261, 280],
    emoji: "🎪",
    color: "#f472b6",
    unlockLevel: 261,
    position: { x: 42, y: 32 },
    region: "magical"
  },
  {
    id: 16,
    name: "Pirate Shipwreck",
    subtitle: "Sunken Treasure",
    levels: [281, 300],
    emoji: "🏴‍☠️",
    color: "#78716c",
    unlockLevel: 281,
    position: { x: 58, y: 38 },
    region: "magical"
  },
  
  // === EASTERN REGION (Right side, going up) ===
  {
    id: 17,
    name: "Cherry Blossom Temple",
    subtitle: "Zen Harmony",
    levels: [301, 320],
    emoji: "🌸",
    color: "#f9a8d4",
    unlockLevel: 301,
    position: { x: 75, y: 32 },
    region: "eastern"
  },
  {
    id: 18,
    name: "Storm Kingdom",
    subtitle: "Thunder & Lightning",
    levels: [321, 340],
    emoji: "⚡",
    color: "#facc15",
    unlockLevel: 321,
    position: { x: 88, y: 40 },
    region: "eastern"
  },
  {
    id: 19,
    name: "Mushroom Cavern",
    subtitle: "Fungal Forest",
    levels: [341, 360],
    emoji: "🍄",
    color: "#a855f7",
    unlockLevel: 341,
    position: { x: 82, y: 25 },
    region: "eastern"
  },
  
  // === ELITE REGION (Top area) ===
  {
    id: 20,
    name: "Casino Royale",
    subtitle: "High Stakes",
    levels: [361, 380],
    emoji: "🎰",
    color: "#fbbf24",
    unlockLevel: 361,
    position: { x: 65, y: 18 },
    region: "elite"
  },
  {
    id: 21,
    name: "Cosmic Nebula",
    subtitle: "Final Frontier",
    levels: [381, 400],
    emoji: "🌌",
    color: "#7c3aed",
    unlockLevel: 381,
    position: { x: 50, y: 10 },
    region: "elite"
  },
  
  // === EXCLUSIVE WORLDS ===
  {
    id: 99,
    name: "Diamond Sky Palace",
    subtitle: "Member Exclusive",
    levels: [9001, 9050],
    emoji: "💎",
    color: "#38bdf8",
    unlockLevel: 1,
    position: { x: 30, y: 18 },
    memberExclusive: true,
    special: true,
    region: "exclusive"
  },
  {
    id: 100,
    name: "Throne Room of Ages",
    subtitle: "VIP Elite • 12x12",
    levels: [8001, 8050],
    emoji: "👑",
    color: "#d4af37",
    unlockLevel: 1,
    position: { x: 15, y: 28 },
    memberExclusive: true,
    vipExclusive: true,
    special: true,
    boardSize: 12,
    region: "exclusive"
  }
];

// Define path styles for different segments
const PATH_STYLES = {
  starter: { color: "rgba(34, 197, 94, 0.6)", width: 0.5 },
  tropical: { color: "rgba(234, 179, 8, 0.6)", width: 0.5 },
  mystic: { color: "rgba(139, 92, 246, 0.6)", width: 0.4 },
  ancient: { color: "rgba(180, 83, 9, 0.5)", width: 0.4 },
  magical: { color: "rgba(236, 72, 153, 0.5)", width: 0.4 },
  eastern: { color: "rgba(249, 168, 212, 0.5)", width: 0.4 },
  elite: { color: "rgba(124, 58, 237, 0.6)", width: 0.5 },
  exclusive: { color: "rgba(168, 85, 247, 0.4)", width: 0.3 }
};

export function WorldSelector({ completedLevels = [], currentLevel = 1, onSelectWorld, onClose, isPremiumMember = false }) {
  const [selectedWorld, setSelectedWorld] = useState(null);
  const [hoveredWorld, setHoveredWorld] = useState(null);
  const mapRef = useRef(null);
  
  // Find current world based on current level
  const getCurrentWorld = () => {
    return WORLDS.find(w => currentLevel >= w.levels[0] && currentLevel <= w.levels[1]) || WORLDS[0];
  };
  
  const currentWorld = getCurrentWorld();
  
  // Calculate world progress
  const getWorldProgress = (world) => {
    if (world.vipExclusive) {
      // For VIP world, check completed VIP levels (8001-8050)
      const completed = completedLevels.filter(l => l >= 8001 && l <= 8050).length;
      return { completed, total: 50 };
    }
    if (world.memberExclusive) {
      // For member world, check completed member levels (9001-9050)
      const completed = completedLevels.filter(l => l >= 9001 && l <= 9050).length;
      return { completed, total: 50 };
    }
    const worldLevels = LEVELS.filter(l => l.id >= world.levels[0] && l.id <= world.levels[1]);
    const completed = worldLevels.filter(l => completedLevels.includes(l.id)).length;
    return { completed, total: worldLevels.length };
  };
  
  // Check if world is unlocked
  const isWorldUnlocked = (world) => {
    // VIP and Member exclusive worlds require premium membership
    if (world.vipExclusive || world.memberExclusive) {
      return isPremiumMember;
    }
    return currentLevel >= world.unlockLevel || completedLevels.includes(world.unlockLevel - 1);
  };
  
  // Handle world click
  const handleWorldClick = (world) => {
    if (isWorldUnlocked(world)) {
      setSelectedWorld(world);
    }
  };
  
  // Travel to selected world
  const handleTravel = () => {
    if (selectedWorld && onSelectWorld) {
      onSelectWorld(selectedWorld); // Pass the entire world object
      onClose();
    }
  };

  // Generate path between worlds with varied curves
  const generatePaths = () => {
    const paths = [];
    const mainWorlds = WORLDS.filter(w => !w.special);
    const specialWorlds = WORLDS.filter(w => w.special);
    
    // Main path connections
    for (let i = 0; i < mainWorlds.length - 1; i++) {
      const from = mainWorlds[i];
      const to = mainWorlds[i + 1];
      const isUnlocked = isWorldUnlocked(to);
      const region = to.region || 'default';
      
      paths.push({ 
        from, 
        to, 
        unlocked: isUnlocked, 
        id: `path-${i}`,
        region,
        style: PATH_STYLES[region] || PATH_STYLES.starter
      });
    }
    
    // Special world connections (branching from nearby main worlds)
    specialWorlds.forEach((specialWorld, idx) => {
      // Connect Crystal Paradise from Cosmic Nebula
      if (specialWorld.id === 99) {
        const connectFrom = mainWorlds.find(w => w.id === 21); // Cosmic Nebula
        if (connectFrom) {
          paths.push({
            from: connectFrom,
            to: specialWorld,
            unlocked: isWorldUnlocked(specialWorld),
            id: `special-path-${idx}`,
            region: 'exclusive',
            style: PATH_STYLES.exclusive,
            special: true
          });
        }
      }
      // Connect Royal Citadel from Moonlight Garden
      if (specialWorld.id === 100) {
        const connectFrom = mainWorlds.find(w => w.id === 13); // Moonlight Garden
        if (connectFrom) {
          paths.push({
            from: connectFrom,
            to: specialWorld,
            unlocked: isWorldUnlocked(specialWorld),
            id: `special-path-${idx}`,
            region: 'exclusive',
            style: PATH_STYLES.exclusive,
            special: true
          });
        }
      }
    });
    
    return paths;
  };

  // Generate a curved path with variation
  const generatePathD = (from, to, index) => {
    const x1 = from.position.x;
    const y1 = from.position.y;
    const x2 = to.position.x;
    const y2 = to.position.y;
    
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    // Vary curve intensity based on distance and index
    const curveIntensity = (index % 3 === 0) ? 0.4 : (index % 3 === 1) ? 0.25 : 0.15;
    const curveDirection = index % 2 === 0 ? 1 : -1;
    
    // Calculate perpendicular offset
    const perpX = dist > 0 ? (-dy / dist) * dist * curveIntensity * curveDirection : 0;
    const perpY = dist > 0 ? (dx / dist) * dist * curveIntensity * curveDirection : 0;
    
    // Control point with offset
    const midX = (x1 + x2) / 2 + perpX;
    const midY = (y1 + y2) / 2 + perpY;
    
    // For longer distances, use cubic bezier for S-curves
    if (dist > 25) {
      const ctrl1X = x1 + dx * 0.25 + perpX * 0.5;
      const ctrl1Y = y1 + dy * 0.25 + perpY * 0.5;
      const ctrl2X = x1 + dx * 0.75 - perpX * 0.3;
      const ctrl2Y = y1 + dy * 0.75 - perpY * 0.3;
      return `M ${x1} ${y1} C ${ctrl1X} ${ctrl1Y}, ${ctrl2X} ${ctrl2Y}, ${x2} ${y2}`;
    }
    
    return `M ${x1} ${y1} Q ${midX} ${midY} ${x2} ${y2}`;
  };

  return (
    <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-md z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900/80 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">World Map</h2>
            <p className="text-slate-400 text-xs">Tap a location to travel</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-slate-400" />
        </button>
      </div>
      
      {/* Map Container - Scrollable */}
      <div 
        className="flex-1 relative overflow-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800" 
        ref={mapRef}
        style={{ scrollBehavior: 'smooth' }}
      >
        {/* Map Content - Taller for more vertical space */}
        <div className="relative min-h-[800px] md:min-h-[600px]" style={{ minWidth: '100%' }}>
          {/* Animated Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950">
            {/* Moving gradient overlay */}
            <div 
              className="absolute inset-0 opacity-30"
              style={{
                background: 'radial-gradient(ellipse at 20% 80%, rgba(59, 130, 246, 0.3) 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, rgba(168, 85, 247, 0.3) 0%, transparent 50%)',
                animation: 'gradientMove 15s ease-in-out infinite alternate'
              }}
            />
          </div>
        
        {/* Animated Stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <div
              key={`star-${i}`}
              className="absolute rounded-full bg-white"
              style={{
                width: Math.random() * 3 + 1 + 'px',
                height: Math.random() * 3 + 1 + 'px',
                left: Math.random() * 100 + '%',
                top: Math.random() * 100 + '%',
                opacity: Math.random() * 0.7 + 0.3,
                animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))}
        </div>
        
        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(15)].map((_, i) => (
            <div
              key={`particle-${i}`}
              className="absolute rounded-full"
              style={{
                width: Math.random() * 6 + 4 + 'px',
                height: Math.random() * 6 + 4 + 'px',
                left: Math.random() * 100 + '%',
                background: ['rgba(59, 130, 246, 0.4)', 'rgba(168, 85, 247, 0.4)', 'rgba(34, 197, 94, 0.4)', 'rgba(251, 191, 36, 0.4)'][i % 4],
                filter: 'blur(1px)',
                animation: `float ${10 + Math.random() * 10}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 5}s`
              }}
            />
          ))}
        </div>
        
        {/* Subtle Grid */}
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        
        {/* Connection Paths - Using varied bezier curves */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="0.4" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <filter id="pathGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="0.6" result="blur"/>
              <feMerge>
                <feMergeNode in="blur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            {/* Gradient definitions for paths */}
            <linearGradient id="starterGrad" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(34, 197, 94, 0.8)" />
              <stop offset="100%" stopColor="rgba(6, 182, 212, 0.8)" />
            </linearGradient>
            <linearGradient id="tropicalGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(234, 179, 8, 0.8)" />
              <stop offset="100%" stopColor="rgba(220, 38, 38, 0.8)" />
            </linearGradient>
            <linearGradient id="mysticGrad" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(139, 92, 246, 0.7)" />
              <stop offset="100%" stopColor="rgba(56, 189, 248, 0.7)" />
            </linearGradient>
          </defs>
          
          {generatePaths().map(({ from, to, unlocked, id, style, special }, index) => {
            const pathD = generatePathD(from, to, index);
            const pathColor = style?.color || "rgba(255,255,255,0.3)";
            const pathWidth = style?.width || 0.4;
            
            return (
              <g key={id}>
                {/* Background glow for unlocked paths */}
                {unlocked && (
                  <path
                    d={pathD}
                    fill="none"
                    stroke={pathColor}
                    strokeWidth={pathWidth * 3}
                    strokeLinecap="round"
                    opacity="0.3"
                    filter="url(#pathGlow)"
                  />
                )}
                
                {/* Main path line */}
                <path
                  d={pathD}
                  fill="none"
                  stroke={unlocked ? pathColor : "rgba(255,255,255,0.1)"}
                  strokeWidth={unlocked ? pathWidth : pathWidth * 0.5}
                  strokeDasharray={unlocked ? (special ? "2,1" : "none") : "0.8,0.8"}
                  strokeLinecap="round"
                />
                
                {/* Dotted overlay for texture on unlocked paths */}
                {unlocked && !special && (
                  <path
                    d={pathD}
                    fill="none"
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth={pathWidth * 0.3}
                    strokeDasharray="0.3,1.5"
                    strokeLinecap="round"
                  />
                )}
                
                {/* Animated traveling dot on unlocked paths */}
                {unlocked && (
                  <circle 
                    r={special ? "0.6" : "0.9"} 
                    fill="white" 
                    opacity="0.95" 
                    filter="url(#glow)"
                  >
                    <animateMotion
                      dur={special ? "6s" : `${3 + (index % 3)}s`}
                      repeatCount="indefinite"
                      path={pathD}
                    />
                  </circle>
                )}
                
                {/* Second dot for longer paths */}
                {unlocked && !special && Math.sqrt(
                  Math.pow(to.position.x - from.position.x, 2) + 
                  Math.pow(to.position.y - from.position.y, 2)
                ) > 20 && (
                  <circle r="0.6" fill={pathColor} opacity="0.8">
                    <animateMotion
                      dur={`${4 + (index % 2)}s`}
                      repeatCount="indefinite"
                      path={pathD}
                      begin="1.5s"
                    />
                  </circle>
                )}
              </g>
            );
          })}
        </svg>
        
        {/* World Nodes */}
        {WORLDS.map((world) => {
          const unlocked = isWorldUnlocked(world);
          const { completed, total } = getWorldProgress(world);
          const isCurrent = world.id === currentWorld.id;
          const isSelected = selectedWorld?.id === world.id;
          const isHovered = hoveredWorld?.id === world.id;
          const progress = total > 0 ? (completed / total) * 100 : 0;
          
          return (
            <button
              key={world.id}
              onClick={() => handleWorldClick(world)}
              onMouseEnter={() => setHoveredWorld(world)}
              onMouseLeave={() => setHoveredWorld(null)}
              disabled={!unlocked}
              className={cn(
                "absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300",
                unlocked ? "cursor-pointer" : "cursor-not-allowed"
              )}
              style={{ 
                left: `${world.position.x}%`, 
                top: `${world.position.y}%`,
                zIndex: isSelected || isHovered ? 20 : 10
              }}
              data-testid={`world-${world.id}`}
            >
              {/* Glow effect for current world */}
              {isCurrent && (
                <div 
                  className="absolute inset-0 rounded-full animate-ping"
                  style={{ 
                    backgroundColor: world.color,
                    opacity: 0.3,
                    transform: 'scale(1.5)'
                  }}
                />
              )}
              
              {/* World Node */}
              <div className={cn(
                "relative flex flex-col items-center gap-1 transition-transform duration-200",
                (isSelected || isHovered) && unlocked && "scale-110"
              )}>
                {/* Container for icon + ring */}
                <div className="relative w-16 h-16 flex items-center justify-center">
                  {/* Progress Ring - Outside the icon */}
                  {unlocked && progress > 0 && (
                    <svg className="absolute inset-0 w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                      {/* Background circle */}
                      <circle
                        cx="32"
                        cy="32"
                        r="30"
                        fill="none"
                        stroke="rgba(255,255,255,0.1)"
                        strokeWidth="4"
                      />
                      {/* Progress arc */}
                      <circle
                        cx="32"
                        cy="32"
                        r="30"
                        fill="none"
                        stroke={world.color}
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeDasharray={`${progress * 1.885} 188.5`}
                        style={{
                          filter: `drop-shadow(0 0 6px ${world.color})`
                        }}
                      />
                    </svg>
                  )}
                  
                  {/* Icon Circle - Centered */}
                  <div 
                    className={cn(
                      "w-12 h-12 rounded-full flex items-center justify-center text-xl relative z-10",
                      "border-2 shadow-lg transition-all duration-200",
                      unlocked 
                        ? "bg-slate-800" 
                        : "bg-slate-900 opacity-50",
                      isSelected && "ring-4 ring-white/50",
                      isCurrent && "ring-2 ring-offset-2 ring-offset-slate-950"
                    )}
                    style={{ 
                      borderColor: unlocked ? world.color : '#475569',
                      boxShadow: unlocked ? `0 0 20px ${world.color}40` : 'none'
                    }}
                  >
                    {unlocked ? (
                      <span>{world.emoji}</span>
                    ) : (
                      <Lock className="w-5 h-5 text-slate-500" />
                    )}
                    
                    {/* Completion check */}
                    {completed === total && total > 0 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-slate-900">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </div>
                
                {/* World Name Label */}
                <div className={cn(
                  "px-2 py-1 rounded-lg text-center transition-all duration-200 min-w-[80px]",
                  unlocked ? "bg-slate-800/90" : "bg-slate-900/80",
                  (isSelected || isHovered) && unlocked && "bg-slate-700/95",
                  world.memberExclusive && "border border-purple-500/50",
                  world.vipExclusive && "border border-yellow-500/50"
                )}>
                  {/* Members Only Badge */}
                  {world.memberExclusive && !world.vipExclusive && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-500 text-white text-[8px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap shadow-lg">
                      ✨ MEMBERS
                    </div>
                  )}
                  {/* VIP Elite Badge */}
                  {world.vipExclusive && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-[8px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap shadow-lg">
                      👑 VIP ELITE
                    </div>
                  )}
                  <p className={cn(
                    "text-xs font-bold truncate",
                    unlocked ? "text-white" : "text-slate-500",
                    world.memberExclusive && unlocked && "text-purple-300",
                    world.vipExclusive && unlocked && "text-yellow-300"
                  )}>
                    {world.name}
                  </p>
                  {unlocked && (
                    <p className="text-[10px] text-slate-400">
                      {completed}/{total}
                    </p>
                  )}
                </div>
              </div>
            </button>
          );
        })}
        
        {/* Current Location Indicator */}
        <div 
          className="absolute transform -translate-x-1/2 pointer-events-none z-30"
          style={{ 
            left: `${currentWorld.position.x}%`, 
            top: `${currentWorld.position.y - 12}%`
          }}
        >
          <div className="flex flex-col items-center animate-bounce">
            <span className="text-2xl">📍</span>
            <span className="text-[10px] text-white font-bold bg-red-500 px-2 py-0.5 rounded-full -mt-1">
              YOU
            </span>
          </div>
        </div>
        </div>{/* End min-h wrapper */}
      </div>
      
      {/* Bottom Panel - Selected World Info */}
      <div className={cn(
        "bg-slate-900/95 border-t border-white/10 transition-all duration-300",
        selectedWorld ? "p-4" : "p-0 h-0 overflow-hidden"
      )}>
        {selectedWorld && (
          <div className="flex items-center gap-4">
            {/* World Icon */}
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shrink-0"
              style={{ backgroundColor: `${selectedWorld.color}20`, borderColor: selectedWorld.color }}
            >
              {selectedWorld.emoji}
            </div>
            
            {/* World Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-white font-bold text-lg">{selectedWorld.name}</h3>
                {/* Membership badge in panel */}
                {selectedWorld.memberExclusive && !selectedWorld.vipExclusive && (
                  <span className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                    ✨ MEMBERS ONLY
                  </span>
                )}
                {selectedWorld.vipExclusive && (
                  <span className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-[10px] px-2 py-0.5 rounded-full font-bold">
                    👑 VIP ELITE
                  </span>
                )}
              </div>
              <p className="text-slate-400 text-sm">{selectedWorld.subtitle}</p>
              <div className="flex items-center gap-2 mt-1">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="text-slate-300 text-sm">
                  {selectedWorld.memberExclusive 
                    ? `50 Exclusive Levels${selectedWorld.boardSize === 12 ? ' • 12×12 Grid' : ''}`
                    : `Levels ${selectedWorld.levels[0]} - ${selectedWorld.levels[1]}`
                  }
                </span>
              </div>
            </div>
            
            {/* Travel Button - or Membership prompt */}
            {isWorldUnlocked(selectedWorld) ? (
              <button
                onClick={handleTravel}
                className={cn(
                  "px-6 py-3 rounded-xl font-bold text-white flex items-center gap-2",
                  selectedWorld.vipExclusive 
                    ? "bg-gradient-to-r from-yellow-500 to-amber-500 text-black shadow-yellow-500/30"
                    : selectedWorld.memberExclusive
                    ? "bg-gradient-to-r from-purple-500 to-pink-500 shadow-purple-500/30"
                    : "bg-gradient-to-r from-blue-500 to-purple-600 shadow-purple-500/30",
                  "hover:opacity-90",
                  "active:scale-95 transition-all duration-200",
                  "shadow-lg"
                )}
              >
                <Plane className="w-5 h-5" />
                Travel
              </button>
            ) : selectedWorld.memberExclusive ? (
              <button
                onClick={() => {/* Navigate to subscription */}}
                className={cn(
                  "px-6 py-3 rounded-xl font-bold flex items-center gap-2",
                  selectedWorld.vipExclusive 
                    ? "bg-gradient-to-r from-yellow-500 to-amber-500 text-black"
                    : "bg-gradient-to-r from-purple-500 to-pink-500 text-white",
                  "hover:opacity-90 active:scale-95 transition-all duration-200 shadow-lg"
                )}
              >
                <Crown className="w-5 h-5" />
                {selectedWorld.vipExclusive ? 'Get VIP' : 'Subscribe'}
              </button>
            ) : (
              <div className="px-6 py-3 rounded-xl font-bold text-slate-500 bg-slate-800 flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Locked
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Hint text when no world selected */}
      {!selectedWorld && (
        <div className="absolute bottom-4 left-0 right-0 text-center">
          <p className="text-slate-500 text-sm">
            Tap on a location to see details
          </p>
        </div>
      )}
      
      {/* CSS Animations */}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-10px) translateX(-10px); }
          75% { transform: translateY(-30px) translateX(5px); }
        }
        @keyframes gradientMove {
          0% { transform: translateX(0) translateY(0); }
          100% { transform: translateX(50px) translateY(-30px); }
        }
      `}</style>
    </div>
  );
}

export default WorldSelector;
